import { useIsDemo } from "@/demo/DemoContext";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { FileText, ReceiptText, Truck, Check, AlertTriangle, CreditCard, X, Plus, CalendarCheck, Sparkles, GitCompareArrows } from "lucide-react";
import { useDolfinClient } from "@/components/DolfinProvider";
import { apiErrorMessage } from "@/lib/api-error";
import { formatCurrency, formatDate } from "@/lib/format";
import { autoConfidence, isAutoMatched, isAutoResolved } from "@/lib/match-auto";
import { BackLink, PageContainer, PageHeader } from "@/components/PagePrimitives";
import { EmptyState, ErrorState, Eyebrow, LoadingState, ScoreMeter, TH } from "@/components/PageUi";
import { InfoHint } from "@/components/InfoHint";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/lib/utils";
import { toast } from "sonner";
import type { components } from "@dolfin/api-public";

type MatchGroupView = components["schemas"]["MatchGroupView"];
type MatchLineView = components["schemas"]["MatchLineView"];
type MatchSuggestionView = components["schemas"]["MatchSuggestionView"];
type MatchDnSuggestionView = components["schemas"]["MatchDnSuggestionView"];
type BillResponse = components["schemas"]["BillResponse"];
type PurchaseOrderResponse = components["schemas"]["PurchaseOrderResponse"];
type DeliveryNoteResponse = components["schemas"]["DeliveryNoteResponse"];

// The suggester proposes only ONE PO and ONE DN per bill line, so a second PO or delivery (split order or
// split delivery) can only be matched by picking it here.
type PoLineOption = {
  poId: string;
  poNumber: string | null;
  lineId: string;
  description: string;
  sku: string | null;
  quantity: number;
  unitPrice: number;
};
type DnLineOption = {
  dnId: string;
  dnNumber: string | null;
  lineId: string;
  description: string;
  sku: string | null;
  quantity: number;
};
// One generic Accept settles any variance kind; the kind stays derived on the line.
type ResolutionAction = components["schemas"]["MatchResolutionAction"];

const STATUS_META: Record<string, { label: string; tone: Tone }> = {
  Open: { label: "Needs review", tone: "amber" },
  Variance: { label: "Needs review", tone: "amber" },
  Resolved: { label: "Matched", tone: "blue" },
  Synced: { label: "Synced", tone: "muted" },
};

type Tone = "amber" | "blue" | "destructive" | "primary" | "muted";

const TONE: Record<Tone, string> = {
  amber: "bg-amber-500/15 text-amber-600 border-amber-500/30",
  blue: "bg-accent-blue/10 text-accent-blue border-accent-blue/25",
  destructive: "bg-destructive text-white border-destructive",
  primary: "bg-primary text-primary-foreground border-primary",
  muted: "bg-muted text-muted-foreground border-border",
};

export function MatchPage({
  billId,
  onBack,
  onPurchaseOrderClick,
  onDeliveryNoteClick,
  onViewBill,
}: {
  billId: string;
  currency?: string;
  onBack?: () => void;
  onPurchaseOrderClick?: (purchaseOrderId: string) => void;
  onDeliveryNoteClick?: (deliveryNoteId: string) => void;
  onViewBill?: () => void;
}) {
  const api = useDolfinClient();
  const [view, setView] = useState<MatchGroupView | null>(null);
  const [bill, setBill] = useState<BillResponse | null>(null);
  // The supplier's open purchase orders / delivery notes — the pool the manual allocation picker draws from.
  const [purchaseOrders, setPurchaseOrders] = useState<PurchaseOrderResponse[]>([]);
  const [deliveryNotes, setDeliveryNotes] = useState<DeliveryNoteResponse[]>([]);
  // FIRST PAINT ONLY: the page spinner unmounts every row (losing scroll and any open picker). Refreshes use `refreshing`.
  const [loading, setLoading] = useState(true);
  // A failed first load (anything but "no match yet") gets its own state with a retry, rather than
  // reading as "no three-way match exists".
  const [loadError, setLoadError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [acting, setActing] = useState(false);
  // AP settings are client-scoped (unreadable here), so this is the engine default; ranking happens server-side.
  const [dateWindowDays] = useState(7);

  // Loaded lazily on first "Match manually" open (see ensurePoDn); most reviewers never open the picker.
  const poDnLoadedRef = useRef(false);
  const poDnInFlightRef = useRef(false);
  const [poDnLoading, setPoDnLoading] = useState(false);

  const fetchPoDn = useCallback(
    async (b: BillResponse | null) => {
      // The EFFECTIVE supplier: in PendingReview supplierId is still null and only the suggestion is set.
      // Mirrors the engine's SupplierId ?? SuggestedMatchedSupplierId.
      const supplierId = (b?.supplierId ?? b?.suggestedMatchedSupplierId) ?? undefined;
      if (!supplierId) {
        setPurchaseOrders([]);
        setDeliveryNotes([]);
        return;
      }
      const [pos, dns] = await Promise.all([
        api.GET("/v1/purchase-orders", { params: { query: { SupplierId: supplierId, Limit: 100 } } }),
        api.GET("/v1/delivery-notes", { params: { query: { SupplierId: supplierId, Limit: 100 } } }),
      ]);
      setPurchaseOrders(pos.error ? [] : (pos.data?.data ?? []));
      setDeliveryNotes(dns.error ? [] : (dns.data?.data ?? []));
    },
    [api],
  );

  const loadAll = useCallback(async () => {
    setLoading(true);
    const [match, billRes] = await Promise.all([
      api.GET("/v1/bills/{billId}/match-group", { params: { path: { billId } } }),
      api.GET("/v1/bills/{id}", { params: { path: { id: billId } } }),
    ]);
    setView(match.error ? null : (match.data ?? null));
    setBill(billRes.error ? null : (billRes.data ?? null));
    setLoadError(
      match.error && match.response.status !== 404
        ? apiErrorMessage(match.error, "Couldn't load the match.")
        : null,
    );
    setLoading(false);
  }, [api, billId]);

  useEffect(() => {
    loadAll();
  }, [loadAll]);

  // Re-pull only what a match write changes, in place: the match group, and the PO/DN pool if already loaded.
  // Not the bill: the match flow never mutates it.
  const refresh = useCallback(async () => {
    setRefreshing(true);
    const { data, error, response } = await api.GET("/v1/bills/{billId}/match-group", {
      params: { path: { billId } },
    });
    // Keep the last good view on failure, except a 404, where the group really is gone.
    if (response.status === 404) setView(null);
    else if (error) toast.error(apiErrorMessage(error, "Failed to refresh match"));
    else setView(data ?? null);
    if (poDnLoadedRef.current) await fetchPoDn(bill);
    setRefreshing(false);
  }, [api, billId, fetchPoDn, bill]);

  // Ref-guarded so several pickers opening in one tick fetch once.
  const ensurePoDn = useCallback(async () => {
    if (poDnLoadedRef.current || poDnInFlightRef.current) return;
    poDnInFlightRef.current = true;
    setPoDnLoading(true);
    await fetchPoDn(bill);
    poDnLoadedRef.current = true;
    poDnInFlightRef.current = false;
    setPoDnLoading(false);
  }, [fetchPoDn, bill]);

  async function resolve(billLineId: string, action: ResolutionAction) {
    setActing(true);
    const { error } = await api.POST("/v1/match-groups/{id}/resolve", {
      params: { path: { id: view!.id } },
      body: { billLineId, action, notes: null },
    });
    if (error) toast.error(apiErrorMessage(error, "Failed to resolve"));
    await refresh();
    setActing(false);
  }

  async function unresolve(billLineId: string) {
    setActing(true);
    const { error } = await api.DELETE("/v1/match-groups/{id}/resolutions/{billLineId}", {
      params: { path: { id: view!.id, billLineId } },
    });
    if (error) toast.error(apiErrorMessage(error, "Failed to undo"));
    await refresh();
    setActing(false);
  }

  // No method, confidence or quantity: the server derives them from its own suggestions, and the client can't
  // see what other bills have already taken from the order.
  async function acceptPoSuggestion(billLineId: string, poLineId: string) {
    setActing(true);
    const { error } = await api.POST("/v1/match-groups/{id}/allocations", {
      params: { path: { id: view!.id } },
      body: { billLineId, poLineId },
    });
    if (error) toast.error(apiErrorMessage(error, "Failed to match purchase order"));
    else toast.success("Purchase-order line matched");
    await refresh();
    setActing(false);
  }

  // Matched as its own receipt-only allocation, never fused with a PO row, so each stays removable.
  async function acceptDnSuggestion(billLineId: string, dnLineId: string) {
    setActing(true);
    const { error } = await api.POST("/v1/match-groups/{id}/allocations", {
      params: { path: { id: view!.id } },
      body: { billLineId, dnLineId },
    });
    if (error) toast.error(apiErrorMessage(error, "Failed to match delivery note"));
    else toast.success("Delivery note matched");
    await refresh();
    setActing(false);
  }

  // The escape hatch for multi-PO / multi-DN; the backend's double-allocation guard still backstops it.
  async function addManualAllocation(
    billLineId: string,
    poLineId: string | null,
    dnLineId: string | null,
    quantity: number,
  ) {
    setActing(true);
    const targets: Array<{ poLineId: string | null; dnLineId: string | null }> = [];
    if (poLineId) targets.push({ poLineId, dnLineId: null });
    if (dnLineId) targets.push({ poLineId: null, dnLineId });

    let failed = false;
    for (const t of targets) {
      const { error } = await api.POST("/v1/match-groups/{id}/allocations", {
        params: { path: { id: view!.id } },
        // Nor here: the server works out the counterpart was never suggested, so "manual" is a fact, not a label.
        body: { billLineId, ...t, matchedQuantity: quantity },
      });
      if (error) {
        failed = true;
        toast.error(apiErrorMessage(error, "Failed to add match"));
      }
    }
    if (!failed) toast.success("Match added");
    await refresh();
    setActing(false);
  }

  async function unallocate(allocationId: string) {
    setActing(true);
    const { error } = await api.DELETE("/v1/match-groups/{id}/allocations/{allocationId}", {
      params: { path: { id: view!.id, allocationId } },
    });
    if (error) toast.error(apiErrorMessage(error, "Failed to unmatch"));
    else toast.success("Unmatched");
    await refresh();
    setActing(false);
  }

  // Declared before any early return so hook order stays stable.
  const poLineOptions = useMemo<PoLineOption[]>(
    () =>
      purchaseOrders
        .filter((po) => po.status === "Open" || po.status === "PartiallyMatched")
        .flatMap((po) =>
          (po.lines ?? []).map((l) => ({
            poId: po.id!,
            poNumber: po.poNumber ?? null,
            lineId: l.id!,
            description: l.description!,
            sku: l.sku ?? null,
            quantity: Number(l.quantityOrdered ?? 0),
            unitPrice: Number(l.unitPrice ?? 0),
          })),
        ),
    [purchaseOrders],
  );
  const dnLineOptions = useMemo<DnLineOption[]>(
    () =>
      deliveryNotes
        .filter((dn) => dn.status === "Open" || dn.status === "PartiallyMatched")
        .flatMap((dn) =>
          (dn.lines ?? []).map((l) => ({
            dnId: dn.id!,
            dnNumber: dn.dnNumber ?? null,
            lineId: l.id!,
            description: l.description!,
            sku: l.sku ?? null,
            quantity: Number(l.quantityReceived ?? 0),
          })),
        ),
    [deliveryNotes],
  );

  // The engine ranks these up; tag their suggestions so the reviewer sees why.
  const inWindowPoIds = useMemo(() => {
    const invoiceDate = bill?.invoiceDate ? new Date(bill.invoiceDate) : null;
    const ids = new Set<string>();
    if (!invoiceDate || Number.isNaN(invoiceDate.getTime())) return ids;
    const windowMs = dateWindowDays * 24 * 60 * 60 * 1000;
    for (const po of purchaseOrders) {
      if (!po.id || !po.expectedDeliveryDate) continue;
      const expected = new Date(po.expectedDeliveryDate);
      if (Number.isNaN(expected.getTime())) continue;
      if (Math.abs(expected.getTime() - invoiceDate.getTime()) <= windowMs) ids.add(po.id);
    }
    return ids;
  }, [bill?.invoiceDate, purchaseOrders, dateWindowDays]);

  const back = onBack && <BackLink onClick={onBack}>Back to bill</BackLink>;

  if (loading) {
    return (
      <PageContainer>
        {back}
        <LoadingState label="Loading match..." />
      </PageContainer>
    );
  }

  if (loadError) {
    return (
      <PageContainer>
        {back}
        <ErrorState message={loadError} onRetry={loadAll} />
      </PageContainer>
    );
  }

  if (!view) {
    return (
      <PageContainer>
        {back}
        <EmptyState
          icon={GitCompareArrows}
          title="No three-way match yet"
          action={
            onViewBill ? (
              <Button variant="outline" size="sm" onClick={onViewBill}>
                Open the bill
              </Button>
            ) : undefined
          }
        >
          A match is created once the bill quotes a purchase order or a delivery note arrives for its
          supplier.
        </EmptyState>
      </PageContainer>
    );
  }

  const currency = bill?.currency ?? "GBP";
  const isDemo = useIsDemo();
  const editable = !isDemo && bill?.state === "PendingReview";
  const status = STATUS_META[view.status] ?? { label: view.status, tone: "muted" as Tone };

  const poIds = new Set<string>();
  const dnIds = new Set<string>();
  let billedValue = 0;
  let orderedValue = 0;
  let totalBilledQty = 0;
  let totalOrderedQty = 0;
  let totalReceivedQty = 0;
  let receiptShort = 0;
  for (const line of view.lines) {
    billedValue += Number(line.billedNet);
    totalBilledQty += Number(line.billedQuantity);
    const po = lineOrdered(line);
    totalOrderedQty += po.qty;
    orderedValue += po.value;
    if (line.receivedQuantity != null) {
      totalReceivedQty += Number(line.receivedQuantity);
      const shortfall = Number(line.billedQuantity) - Number(line.receivedQuantity);
      if (shortfall > 0) receiptShort += shortfall;
    } else {
      totalReceivedQty += Number(line.billedQuantity); // no DN dimension → treat as received for totals
    }
    line.allocations.forEach((a) => {
      if (a.poId) poIds.add(a.poId);
      if (a.dnId) dnIds.add(a.dnId);
    });
  }
  const reconciledCount = view.lines.filter((l) => lineReconciled(l)).length;

  const summary = deriveSummary(view);

  return (
    <PageContainer>
      {back}

      <PageHeader
        eyebrow="Payables · 3-way match"
        title={bill?.supplierName ?? "Supplier"}
        subtitle={
          <span className="flex flex-wrap items-center gap-x-2 gap-y-1">
            {bill?.totalAmount != null ? (
              <span className="text-base font-semibold text-foreground tabular-nums">
                {formatCurrency(bill.totalAmount, currency)}
              </span>
            ) : null}
            {bill?.invoiceNumber ? (
              onViewBill ? (
                <button
                  type="button"
                  onClick={onViewBill}
                  className="font-medium text-primary hover:underline"
                >
                  {bill.invoiceNumber}
                </button>
              ) : (
                <span>{bill.invoiceNumber}</span>
              )
            ) : null}
            {bill?.invoiceDate ? (
              <>
                <span aria-hidden>·</span>
                <span>Invoiced {formatDate(bill.invoiceDate)}</span>
              </>
            ) : null}
          </span>
        }
        actions={
          <>
            {/* A small header cue, not a page spinner, so the rows stay put. */}
            {refreshing && (
              <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground" aria-live="polite">
                <Spinner className="size-3.5" /> Updating
              </span>
            )}
            <Pill tone={status.tone}>
              <span className="size-[7px] rounded-full bg-current" />
              {status.label}
            </Pill>
          </>
        }
      />

      <div className="mb-4 grid grid-cols-1 gap-4 md:grid-cols-3">
        <DocCard
          icon={<FileText className="size-5" />}
          eyebrow="Purchase order"
          title={poIds.size === 0 ? "None matched" : poIds.size === 1 ? "1 order" : `${poIds.size} orders`}
          footerLabel="Ordered value"
          footerValue={formatCurrency(orderedValue, currency)}
          meta={`${totalOrderedQty} units matched`}
        />
        <DocCard
          icon={<ReceiptText className="size-5" />}
          eyebrow="Supplier bill"
          title={bill?.invoiceNumber || "Bill"}
          footerLabel="Billed value"
          footerValue={formatCurrency(bill?.subTotal ?? billedValue, currency)}
          footerSub="exc. tax"
          meta={bill?.dueDate ? `Due ${formatDate(bill.dueDate)}` : "No due date"}
        />
        <DocCard
          icon={<Truck className="size-5" />}
          eyebrow="Delivery note"
          title={dnIds.size === 0 ? "Not received" : dnIds.size === 1 ? "1 delivery" : `${dnIds.size} deliveries`}
          footerLabel="Units received"
          footerValue={
            <span>
              <span className={receiptShort > 0 ? "text-amber-600" : undefined}>{totalReceivedQty}</span>
              <span className="font-medium text-muted-foreground"> / {totalBilledQty}</span>
            </span>
          }
          badge={receiptShort > 0 ? `${receiptShort} short` : undefined}
          meta="Received goods"
        />
      </div>

      <div className="mb-4 overflow-hidden rounded-xl border bg-card/60 backdrop-blur-sm">
        <div className={cn("grid grid-cols-[2.2fr_1fr_0.8fr_0.8fr_1fr_1.2fr] bg-muted/40 px-5 py-3 font-medium text-muted-foreground", TH)}>
          <div>Item</div>
          <div className="text-right">Unit price</div>
          <div className="text-right">Ordered</div>
          <div className="text-right">Billed</div>
          <div className="text-right">Received</div>
          <div className="text-right">Match</div>
        </div>

        {view.lines.map((line) => (
          <LineRow
            key={line.billLineId}
            line={line}
            currency={currency}
            editable={editable ?? false}
            acting={acting}
            poSuggestions={view.suggestions.filter((s) => s.billLineId === line.billLineId)}
            dnSuggestions={view.dnSuggestions.filter((s) => s.billLineId === line.billLineId)}
            inWindowPoIds={inWindowPoIds}
            poLineOptions={poLineOptions}
            dnLineOptions={dnLineOptions}
            poDnLoading={poDnLoading}
            onEnsurePoDn={ensurePoDn}
            onResolve={resolve}
            onUnresolve={unresolve}
            onAcceptPoSuggestion={acceptPoSuggestion}
            onAcceptDnSuggestion={acceptDnSuggestion}
            onAddManualAllocation={addManualAllocation}
            onUnallocate={unallocate}
            onPurchaseOrderClick={onPurchaseOrderClick}
            onDeliveryNoteClick={onDeliveryNoteClick}
          />
        ))}

        <div className="grid grid-cols-[2.2fr_1fr_0.8fr_0.8fr_1fr_1.2fr] items-center border-t bg-muted/40 px-5 py-3.5">
          <Eyebrow>Totals</Eyebrow>
          <div />
          <div className="text-right font-mono text-[15px] font-bold tabular-nums">{totalOrderedQty}</div>
          <div className="text-right font-mono text-[15px] font-bold tabular-nums">{totalBilledQty}</div>
          <div
            className={`text-right font-mono text-[15px] font-bold tabular-nums ${receiptShort > 0 ? "text-amber-600" : ""}`}
          >
            {totalReceivedQty}
          </div>
          <div />
        </div>
      </div>

      <div className="flex flex-wrap items-center justify-between gap-5 rounded-xl border bg-card/60 p-5 backdrop-blur-sm">
        <div className="flex items-start gap-3.5">
          <span
            className={`inline-flex size-[38px] flex-none items-center justify-center rounded-[10px] border ${TONE[summary.tone]}`}
          >
            {summary.icon}
          </span>
          <div>
            <div className="text-base font-semibold">{summary.title}</div>
            <div className="mt-0.5 max-w-2xl text-sm leading-relaxed text-muted-foreground">{summary.body}</div>
          </div>
        </div>
        <div className="flex-none text-right">
          <Eyebrow className="mb-0.5">Lines reconciled</Eyebrow>
          <div className="text-2xl font-bold tabular-nums">
            <span className={reconciledCount < view.lines.length ? "text-amber-600" : undefined}>
              {reconciledCount}
            </span>
            <span className="font-medium text-muted-foreground"> / {view.lines.length}</span>
          </div>
        </div>
      </div>
    </PageContainer>
  );
}

function LineRow({
  line,
  currency,
  editable,
  acting,
  poSuggestions,
  dnSuggestions,
  inWindowPoIds,
  poLineOptions,
  dnLineOptions,
  poDnLoading,
  onEnsurePoDn,
  onResolve,
  onUnresolve,
  onAcceptPoSuggestion,
  onAcceptDnSuggestion,
  onAddManualAllocation,
  onUnallocate,
  onPurchaseOrderClick,
  onDeliveryNoteClick,
}: {
  line: MatchLineView;
  currency: string;
  editable: boolean;
  acting: boolean;
  poSuggestions: MatchSuggestionView[];
  dnSuggestions: MatchDnSuggestionView[];
  inWindowPoIds: Set<string>;
  poLineOptions: PoLineOption[];
  dnLineOptions: DnLineOption[];
  poDnLoading: boolean;
  onEnsurePoDn: () => void;
  onResolve: (billLineId: string, action: ResolutionAction) => void;
  onUnresolve: (billLineId: string) => void;
  onAcceptPoSuggestion: (billLineId: string, poLineId: string) => void;
  onAcceptDnSuggestion: (billLineId: string, dnLineId: string) => void;
  onAddManualAllocation: (billLineId: string, poLineId: string | null, dnLineId: string | null, qty: number) => void;
  onUnallocate: (allocationId: string) => void;
  onPurchaseOrderClick?: (id: string) => void;
  onDeliveryNoteClick?: (id: string) => void;
}) {
  const po = lineOrdered(line);
  const poAlloc = line.allocations.find((a) => a.poId);
  const unitPrice = poAlloc?.orderedUnitPrice ?? line.billedUnitPrice;
  // Units still needing a PO allocation — what a residual (second-PO) suggestion should bind, not the whole line.
  const residualUnits = Math.max(0, Number(line.billedQuantity) - Number(line.allocatedQuantity));
  // Units still needing receipt reconciliation — what a residual (second-DN) suggestion should bind.
  const residualReceipt = Math.max(0, Number(line.billedQuantity) - Number(line.receivedQuantity ?? 0));
  // PO (price) and DN (receipt) allocations are stored separately — split them onto their own rows.
  const poAllocs = line.allocations.filter((a) => a.poId);
  const dnAllocs = line.allocations.filter((a) => a.dnId);
  const received = line.receivedQuantity == null ? null : Number(line.receivedQuantity);
  const qtyVar = line.quantityVariance == null ? null : Number(line.quantityVariance);
  const hasVariance = line.hasPriceVariance || line.hasQuantityVariance || line.hasOverBillingVariance;
  // requiresReview is the server's own answer, so "needs review" and "this row has something to act on" can't
  // disagree (all three variance flags can be false on a held line). `|| resolution` keeps the strip, and Undo, after a decision.
  const needsAction = line.requiresReview || Boolean(line.resolution);
  const showSuggestions = editable && (poSuggestions.length > 0 || dnSuggestions.length > 0);

  const pill = matchPill(line);
  // Amber is requiresReview outright; blue is "there was a difference, someone settled it".
  const rowTint =
    line.resolution?.action === "Dispute"
      ? "bg-destructive/5"
      : line.requiresReview
        ? "bg-amber-500/[0.07]"
        : hasVariance
          ? "bg-accent-blue/5"
          : "";

  return (
    <div className={`border-t ${rowTint}`}>
      <div className="grid grid-cols-[2.2fr_1fr_0.8fr_0.8fr_1fr_1.2fr] items-center px-5 py-4">
        <div>
          <div className="text-[15px] font-semibold">{line.description}</div>
        </div>
        <div className="text-right font-mono text-sm tabular-nums">{formatCurrency(unitPrice, currency)}</div>
        <div className="text-right font-mono text-[15px] tabular-nums">{po.qty || "-"}</div>
        <div className="text-right font-mono text-[15px] tabular-nums">{Number(line.billedQuantity)}</div>
        <div className="flex items-center justify-end gap-2 text-right font-mono text-[15px] tabular-nums">
          {received == null ? (
            <span className="text-muted-foreground">-</span>
          ) : (
            <>
              <span className={qtyVar && qtyVar > 0 ? "font-bold text-amber-600" : "font-bold"}>{received}</span>
              {qtyVar != null && qtyVar > 0 && (
                <span className="rounded-full bg-destructive/12 px-1.5 py-0.5 text-[11px] font-semibold text-destructive">
                  −{qtyVar}
                </span>
              )}
            </>
          )}
        </div>
        <div className="flex justify-end">
          <Pill tone={pill.tone}>
            {pill.icon}
            {pill.label}
          </Pill>
        </div>
      </div>

      {needsAction && (
        <div className="flex flex-wrap items-center justify-between gap-4 px-5 pb-4">
          <div className="flex items-center gap-2.5 text-[13px] text-muted-foreground">
            <span
              className={`inline-flex size-[26px] flex-none items-center justify-center rounded-[7px] border ${TONE[stripTone(line)]}`}
            >
              {stripIcon(line)}
            </span>
            <span className="leading-snug">{stripMessage(line, currency)}</span>
          </div>

          {editable && !line.resolution && (
            <div className="flex flex-wrap gap-2">
              <ActionButton primary onClick={() => onResolve(line.billLineId, "Accept")} disabled={acting}>
                <Check className="size-3.5" /> {acceptLabel(line)}
              </ActionButton>
              <ActionButton onClick={() => onResolve(line.billLineId, "RequestCreditNote")} disabled={acting}>
                <CreditCard className="size-3.5" /> Request credit note
              </ActionButton>
              <ActionButton onClick={() => onResolve(line.billLineId, "Dispute")} disabled={acting}>
                <AlertTriangle className="size-3.5" /> Dispute
              </ActionButton>
            </div>
          )}

          {line.resolution && editable && (
            <button
              type="button"
              disabled={acting}
              onClick={() => onUnresolve(line.billLineId)}
              className="h-[30px] flex-none rounded-[7px] border border-border px-3 text-xs font-semibold text-muted-foreground hover:text-foreground disabled:opacity-50"
            >
              Undo
            </button>
          )}
        </div>
      )}

      {showSuggestions && (
        <div className="space-y-1.5 px-5 pb-4">
          <Eyebrow>Suggested matches</Eyebrow>
          {poSuggestions.map((s) => (
            <SuggestionRow
              key={`po:${s.poLineId}`}
              icon={<FileText className="size-3.5" />}
              label={s.poNumber ?? "Purchase order"}
              detail={`${Number(s.quantityOrdered)} ordered @ ${formatCurrency(s.orderedUnitPrice, currency)}`}
              confidence={Number(s.confidence)}
              inWindow={inWindowPoIds.has(s.poId)}
              acting={acting}
              onLabelClick={onPurchaseOrderClick ? () => onPurchaseOrderClick(s.poId) : undefined}
              onMatch={() =>
                onAcceptPoSuggestion(
                  s.billLineId,
                  s.poLineId,
                )
              }
            />
          ))}
          {dnSuggestions.map((s) => (
            <SuggestionRow
              key={`dn:${s.dnLineId}`}
              icon={<Truck className="size-3.5" />}
              label={s.dnNumber ?? "Delivery note"}
              detail={`${Number(s.quantityReceived)} received`}
              confidence={Number(s.confidence)}
              acting={acting}
              onLabelClick={onDeliveryNoteClick ? () => onDeliveryNoteClick(s.dnId) : undefined}
              onMatch={() =>
                onAcceptDnSuggestion(
                  s.billLineId,
                  s.dnLineId,
                )
              }
            />
          ))}
        </div>
      )}

      {editable && (
        <ManualAllocate
          line={line}
          currency={currency}
          acting={acting}
          poLineOptions={poLineOptions}
          dnLineOptions={dnLineOptions}
          loadingOptions={poDnLoading}
          onOpenPicker={onEnsurePoDn}
          onAdd={onAddManualAllocation}
        />
      )}

      {/* POs and delivery notes on separate rows, never fused, so each opens and unmatches independently. */}
      {line.allocations.length > 0 && (
        <div className="space-y-1.5 px-5 pb-3 text-xs">
          <div className="flex flex-wrap items-center gap-2">
            {poAllocs.length > 0 ? (
              poAllocs.map((a) => (
                <DocChip
                  key={a.id}
                  icon={<FileText className="size-3" />}
                  label={a.poNumber ?? "PO"}
                  quantity={Number(a.matchedQuantity)}
                  editable={editable}
                  acting={acting}
                  auto={a.autoAccepted}
                  confidence={a.matchConfidence == null ? null : Number(a.matchConfidence)}
                  onOpen={onPurchaseOrderClick && a.poId ? () => onPurchaseOrderClick(a.poId!) : undefined}
                  onRemove={() => onUnallocate(a.id)}
                />
              ))
            ) : (
              <span className="text-muted-foreground">No purchase order matched</span>
            )}
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {dnAllocs.length > 0 ? (
              dnAllocs.map((a) => (
                <DocChip
                  key={a.id}
                  icon={<Truck className="size-3" />}
                  label={a.dnNumber ?? "DN"}
                  quantity={Number(a.matchedQuantity)}
                  editable={editable}
                  acting={acting}
                  auto={a.autoAccepted}
                  confidence={a.matchConfidence == null ? null : Number(a.matchConfidence)}
                  onOpen={onDeliveryNoteClick && a.dnId ? () => onDeliveryNoteClick(a.dnId!) : undefined}
                  onRemove={() => onUnallocate(a.id)}
                />
              ))
            ) : (
              <span className="text-muted-foreground">No delivery note matched</span>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function DocChip({
  icon,
  label,
  quantity,
  editable,
  acting,
  auto,
  confidence,
  onOpen,
  onRemove,
}: {
  auto?: boolean;
  confidence?: number | null;
  icon: React.ReactNode;
  label: string;
  quantity: number;
  editable: boolean;
  acting: boolean;
  onOpen?: () => void;
  onRemove: () => void;
}) {
  return (
    <span className="inline-flex items-center gap-2 rounded-full border border-border bg-background py-1 pl-2.5 pr-1.5">
      <span className="inline-flex items-center gap-1">
        <span className="flex-none text-accent-blue">{icon}</span>
        {onOpen ? (
          <button type="button" className="font-semibold text-primary hover:underline" onClick={onOpen}>
            {label}
          </button>
        ) : (
          <span className="font-semibold">{label}</span>
        )}
      </span>
      <span className="tabular-nums text-muted-foreground">×{quantity}</span>
      {confidence != null && <ConfidenceChip confidence={confidence} />}
      {auto && (
        <span className="inline-flex flex-none items-center gap-0.5 text-[11px] font-medium text-accent-blue">
          <Sparkles className="size-3" aria-hidden /> Auto
        </span>
      )}
      {editable && (
        <button
          type="button"
          disabled={acting}
          onClick={onRemove}
          aria-label="Unmatch"
          title="Unmatch"
          className="inline-flex size-4 flex-none items-center justify-center rounded-full text-muted-foreground hover:bg-destructive/10 hover:text-destructive disabled:opacity-50"
        >
          <X className="size-3" />
        </button>
      )}
    </span>
  );
}

function Pill({ tone, children }: { tone: Tone; children: React.ReactNode }) {
  return (
    <span
      className={`inline-flex items-center gap-1.5 whitespace-nowrap rounded-full border px-2.5 py-1 text-xs font-semibold ${TONE[tone]}`}
    >
      {children}
    </span>
  );
}

function ActionButton({
  children,
  onClick,
  disabled,
  primary,
}: {
  children: React.ReactNode;
  onClick: () => void;
  disabled?: boolean;
  primary?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`inline-flex h-[34px] items-center gap-1.5 rounded-lg px-3.5 text-[13px] font-semibold transition disabled:opacity-50 ${
        primary
          ? "bg-primary text-primary-foreground shadow-sm hover:opacity-90"
          : "border border-border bg-card text-foreground hover:border-accent-blue hover:text-accent-blue"
      }`}
    >
      {children}
    </button>
  );
}

// Same-SKU lines float to the top; quantity defaults to the unallocated units and accepts 0 (a nil delivery).
function ManualAllocate({
  line,
  currency,
  acting,
  poLineOptions,
  dnLineOptions,
  loadingOptions,
  onOpenPicker,
  onAdd,
}: {
  line: MatchLineView;
  currency: string;
  acting: boolean;
  poLineOptions: PoLineOption[];
  dnLineOptions: DnLineOption[];
  loadingOptions: boolean;
  onOpenPicker: () => void;
  onAdd: (billLineId: string, poLineId: string | null, dnLineId: string | null, qty: number) => void;
}) {
  const residual = Math.max(0, Number(line.billedQuantity) - Number(line.allocatedQuantity));
  const [open, setOpen] = useState(false);
  const [poLineId, setPoLineId] = useState("");
  const [dnLineId, setDnLineId] = useState("");
  const [qty, setQty] = useState(String(residual));

  const desc = line.description.trim().toLowerCase();
  const sortByDesc = <T extends { description: string }>(opts: T[]) =>
    [...opts].sort((a, b) => Number(b.description.trim().toLowerCase() === desc) - Number(a.description.trim().toLowerCase() === desc));
  const pos = sortByDesc(poLineOptions);
  const dns = sortByDesc(dnLineOptions);

  // 0 records a nil delivery (the billed quantity becomes a variance); omitted means "take all that's left", so
  // blank must never become 0. InfoHint is a <button>, so it can't sit inside the <label>: hence htmlFor/id.
  const qtyInputId = `manual-qty-${line.billLineId}`;

  const trimmedQty = qty.trim();
  const quantity = Number(trimmedQty);
  const qtyValid = trimmedQty !== "" && Number.isFinite(quantity) && quantity >= 0;
  const canAdd = (poLineId !== "" || dnLineId !== "") && qtyValid;

  function reset() {
    setOpen(false);
    setPoLineId("");
    setDnLineId("");
    setQty(String(residual));
  }

  if (!open) {
    return (
      <div className="px-5 pb-3">
        <button
          type="button"
          disabled={acting}
          onClick={() => {
            onOpenPicker();
            // Seed the quantity from the residual AS OF NOW. The row stays mounted across refreshes, so the
            // initial-state value would otherwise be stale once earlier allocations changed what's left.
            setQty(String(residual));
            setOpen(true);
          }}
          className="inline-flex items-center gap-1.5 rounded-lg border border-dashed border-border px-3 py-1.5 text-[13px] font-semibold text-muted-foreground hover:border-accent-blue hover:text-accent-blue disabled:opacity-50"
        >
          <Plus className="size-3.5" /> Match manually
        </button>
      </div>
    );
  }

  const selectClass =
    "h-9 min-w-0 flex-1 rounded-lg border border-border bg-background px-2.5 text-[13px] focus:border-accent-blue focus:outline-none";

  return (
    <div className="mx-5 mb-3 space-y-2.5 rounded-lg border border-accent-blue/30 bg-accent-blue/[0.04] p-3">
      <Eyebrow>Match manually</Eyebrow>
      <div className="flex flex-wrap items-center gap-2">
        <FileText className="size-3.5 flex-none text-accent-blue" />
        <select className={selectClass} value={poLineId} onChange={(e) => setPoLineId(e.target.value)} disabled={loadingOptions}>
          <option value="">{loadingOptions ? "Loading purchase orders..." : "No purchase order"}</option>
          {pos.map((o) => (
            <option key={o.lineId} value={o.lineId}>
              {(o.poNumber ?? "PO") + " · " + o.description + " · " + o.quantity + " @ " + formatCurrency(o.unitPrice, currency)}
            </option>
          ))}
        </select>
      </div>
      <div className="flex flex-wrap items-center gap-2">
        <Truck className="size-3.5 flex-none text-accent-blue" />
        <select className={selectClass} value={dnLineId} onChange={(e) => setDnLineId(e.target.value)} disabled={loadingOptions}>
          <option value="">{loadingOptions ? "Loading delivery notes..." : "No delivery note"}</option>
          {dns.map((o) => (
            <option key={o.lineId} value={o.lineId}>
              {(o.dnNumber ?? "DN") + " · " + o.description + " · " + o.quantity + " received"}
            </option>
          ))}
        </select>
      </div>
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-[13px] text-muted-foreground">
          <label htmlFor={qtyInputId}>Quantity</label>
          <InfoHint label="Quantity">
            How much of the counterpart this match claims. Enter 0 to record a nil delivery: the line is
            named as expected, none of it arrived, and the whole billed quantity is reported as a variance.
          </InfoHint>
          <input
            id={qtyInputId}
            type="number"
            min={0}
            step="any"
            value={qty}
            onChange={(e) => setQty(e.target.value)}
            aria-invalid={!qtyValid}
            className="h-9 w-24 rounded-lg border border-border bg-background px-2.5 text-right font-mono text-[13px] tabular-nums focus:border-accent-blue focus:outline-none aria-[invalid=true]:border-destructive"
          />
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            disabled={acting}
            onClick={reset}
            className="h-9 rounded-lg border border-border px-3 text-[13px] font-semibold text-muted-foreground hover:text-foreground disabled:opacity-50"
          >
            Cancel
          </button>
          <ActionButton
            primary
            disabled={acting || !canAdd}
            onClick={() => {
              onAdd(line.billLineId, poLineId || null, dnLineId || null, quantity);
              reset();
            }}
          >
            <Check className="size-3.5" /> Add match
          </ActionButton>
        </div>
      </div>
    </div>
  );
}

/** Coloured pill for a fuzzy match confidence — same green/amber/red thresholds as elsewhere. */
function ConfidenceChip({ confidence }: { confidence: number }) {
  if (Number.isNaN(confidence)) return null;
  const pct = Math.round(confidence * 100);
  const cls =
    confidence >= 0.7
      ? "border-emerald-500/25 bg-emerald-500/15 text-emerald-600"
      : confidence >= 0.5
        ? "border-amber-500/25 bg-amber-500/15 text-amber-600"
        : "border-red-500/25 bg-red-500/15 text-red-600";
  return (
    <span
      className={`flex-none rounded-full border px-1.5 py-0.5 text-[11px] font-medium tabular-nums ${cls}`}
    >
      {pct}% match
    </span>
  );
}

function SuggestionRow({
  icon,
  label,
  detail,
  confidence,
  inWindow,
  acting,
  onLabelClick,
  onMatch,
}: {
  icon: React.ReactNode;
  label: string;
  detail: string;
  confidence: number;
  inWindow?: boolean;
  acting: boolean;
  onLabelClick?: () => void;
  onMatch: () => void;
}) {
  return (
    <div className="@container flex items-center justify-between gap-2 rounded-lg border border-dashed bg-background/60 px-3 py-2 text-[13px]">
      <div className="flex min-w-0 items-center gap-2 text-muted-foreground">
        <span className="flex-none text-accent-blue">{icon}</span>
        {onLabelClick ? (
          <button type="button" onClick={onLabelClick} className="flex-none font-semibold text-primary hover:underline">
            {label}
          </button>
        ) : (
          <span className="flex-none font-semibold text-foreground">{label}</span>
        )}
        <span className="truncate">· {detail}</span>
        {inWindow && (
          <span className="flex-none inline-flex items-center gap-1 rounded-full border border-accent-blue/30 bg-accent-blue/10 px-1.5 py-0.5 text-[11px] font-medium text-accent-blue">
            <CalendarCheck className="size-3" />
            In delivery window
          </span>
        )}
      </div>
      <div className="flex flex-none items-center gap-3">
        {/* A bar, not a coloured percentage, as receipt candidates are scored on the expenses pages. */}
        {Number.isNaN(confidence) ? null : <ScoreMeter score={confidence} />}
        <ActionButton primary onClick={onMatch} disabled={acting}>
          <Check className="size-3.5" /> Match
        </ActionButton>
      </div>
    </div>
  );
}

function DocCard({
  icon,
  eyebrow,
  title,
  footerLabel,
  footerValue,
  footerSub,
  meta,
  badge,
}: {
  icon: React.ReactNode;
  eyebrow: string;
  title: string;
  footerLabel: string;
  footerValue: React.ReactNode;
  footerSub?: React.ReactNode;
  meta: string;
  badge?: string;
}) {
  return (
    <div className="relative rounded-xl border bg-card/60 p-5 backdrop-blur-sm">
      {badge && (
        <span className="absolute right-4 top-4 rounded-full border border-amber-500/30 bg-amber-500/15 px-2.5 py-0.5 text-[11px] font-semibold tabular-nums text-amber-600">
          {badge}
        </span>
      )}
      <div className="mb-4 flex items-center gap-3">
        <span className="grid size-10 flex-none place-items-center rounded-xl border bg-muted/50 text-muted-foreground">
          {icon}
        </span>
        <div>
          <Eyebrow>{eyebrow}</Eyebrow>
          <div className="text-[15px] font-semibold">{title}</div>
        </div>
      </div>
      <div className="mb-4 text-[13px] text-muted-foreground">{meta}</div>
      <div className="flex items-baseline justify-between border-t pt-3.5">
        <span className="text-xs text-muted-foreground">{footerLabel}</span>
        <span className="flex flex-col items-end leading-tight">
          <span className="text-[22px] font-bold tabular-nums">{footerValue}</span>
          {footerSub && <span className="text-[11px] font-normal text-muted-foreground">{footerSub}</span>}
        </span>
      </div>
    </div>
  );
}

// "Ordered" for a line = the qty/value matched against PO lines (the view exposes matched qty + ordered unit
// price, not the PO line's full ordered quantity — matched qty is what the chips already show).
function lineOrdered(line: MatchLineView): { qty: number; value: number } {
  let qty = 0;
  let value = 0;
  for (const a of line.allocations) {
    if (!a.poId) continue;
    const q = Number(a.matchedQuantity);
    qty += q;
    value += q * Number(a.orderedUnitPrice ?? 0);
  }
  return { qty, value };
}

function matchPill(line: MatchLineView): { label: string; tone: Tone; icon: React.ReactNode } {
  const action = line.resolution?.action;
  if (action === "Dispute") return { label: "Disputed", tone: "destructive", icon: <AlertTriangle className="size-3" /> };
  if (action === "RequestCreditNote") return { label: "Credit requested", tone: "blue", icon: <CreditCard className="size-3" /> };
  // Distinguish a variance the ENGINE accepted from one a person accepted — the reviewer needs to know which
  // decisions were made on their behalf.
  if (action && isAutoResolved(line))
    return { label: "Auto-accepted", tone: "primary", icon: <Sparkles className="size-3" /> };
  if (action) return { label: "Accepted", tone: "primary", icon: <Check className="size-3" /> };

  if (line.hasQuantityVariance) {
    const v = Number(line.quantityVariance ?? 0);
    return { label: v > 0 ? "Short" : "Over", tone: "amber", icon: <AlertTriangle className="size-3" /> };
  }
  if (line.hasPriceVariance) return { label: "Price", tone: "amber", icon: <AlertTriangle className="size-3" /> };
  // Billed more than the PO ordered: a true surplus (the §6.2 guard caps the PO allocation). Accept, or add a PO.
  if (line.hasOverBillingVariance) return { label: "Surplus", tone: "amber", icon: <AlertTriangle className="size-3" /> };
  if (line.allocations.length === 0) return { label: "Unmatched", tone: "muted", icon: null };
  // Allocated, but the billed net isn't fully covered and there's still capacity to allocate against — NOT a
  // clean match. This is the gap that keeps it out of "lines reconciled".
  if (!line.fullyAllocated) return { label: "Partial", tone: "amber", icon: <AlertTriangle className="size-3" /> };
  // Name the DEGREE of a clean match: PO and DN is three-way; just one is a complete two-way match, not
  // "partial". Engine vs person stays in the icon.
  const hasPo = line.allocations.some((a) => a.poId != null);
  const hasDn = line.allocations.some((a) => a.dnId != null);
  const icon = isAutoMatched(line) ? <Sparkles className="size-3" /> : <Check className="size-3" />;
  if (hasPo && hasDn) return { label: "3-way match", tone: "primary", icon };
  return { label: "2-way match", tone: "blue", icon };
}

function stripTone(line: MatchLineView): Tone {
  const a = line.resolution?.action;
  if (a === "Dispute") return "destructive";
  if (a === "RequestCreditNote") return "blue";
  if (a) return "primary";
  return "amber";
}

function stripIcon(line: MatchLineView) {
  const a = line.resolution?.action;
  if (a === "RequestCreditNote") return <CreditCard className="size-3.5" />;
  if (a && a !== "Dispute") return <Check className="size-3.5" />;
  return <AlertTriangle className="size-3.5" />;
}

// The phrasing for the generic Accept button — the action is always "Accept"; only the label is contextual.
function acceptLabel(line: MatchLineView): string {
  if (line.hasOverBillingVariance) return "Accept surplus";
  // quantityVariance = billed − received: positive is a short delivery (billed more than delivered),
  // negative is an over-delivery (received more than billed). Both trip hasQuantityVariance.
  if (line.hasQuantityVariance)
    return Number(line.quantityVariance ?? 0) > 0 ? "Accept short delivery" : "Accept over-delivery";
  if (line.hasPriceVariance) return "Accept price";
  return "Accept";
}

function stripMessage(line: MatchLineView, currency: string): string {
  const over = Number(line.priceVarianceTotal ?? 0);
  const qtyVar = Number(line.quantityVariance ?? 0);
  const surplus = Number(line.quantityOverOrdered ?? 0);
  const action = line.resolution?.action;
  if (action === "Accept") {
    const what = line.hasOverBillingVariance
      ? `the ${surplus}-unit surplus over the order`
      : line.hasQuantityVariance
        ? qtyVar > 0
          ? "the short delivery"
          : "the over-delivery"
        : `the ${formatCurrency(Math.abs(over), currency)} price variance`;
    // Settled by the engine: say so plainly, and show the score it acted on, so the reviewer can see what was
    // decided for them (and Undo it) rather than wondering who accepted it.
    if (isAutoResolved(line)) {
      const pct = autoConfidence(line);
      return `Automatically accepted ${what}${pct != null ? ` at ${Math.round(pct * 100)}% match` : ""}. No review needed.`;
    }
    if (line.hasOverBillingVariance) return `Accepted the ${surplus}-unit surplus over the order.`;
    if (line.hasQuantityVariance)
      return qtyVar > 0
        ? "Accepted short delivery. Bill stands at the received quantity."
        : "Accepted over-delivery. Bill stands at the billed quantity.";
    return `Accepted the ${formatCurrency(Math.abs(over), currency)} price variance.`;
  }
  if (action === "RequestCreditNote")
    return `Credit note requested from the supplier. Bill paid in full once it arrives.`;
  if (action === "AcceptAndUpdateCatalog") return "Accepted and flagged the catalog price for update.";
  if (action === "Dispute") return "Disputed with the supplier. Payment on this line is on hold.";

  if (line.hasOverBillingVariance)
    return `${surplus} billed over the order of ${Number(line.orderedQuantity ?? 0)}. Accept the surplus, add a second PO, or dispute.`;
  if (line.hasQuantityVariance && qtyVar > 0)
    return `${qtyVar} short on delivery${over ? ` · ${formatCurrency(Math.abs(over), currency)} over-billed` : ""}.`;
  if (line.hasQuantityVariance)
    return `${Math.abs(qtyVar)} over-delivered${over ? ` · ${formatCurrency(Math.abs(over), currency)} over-billed` : ""}.`;
  if (line.hasPriceVariance) return `${formatCurrency(Math.abs(over), currency)} ${over > 0 ? "over" : "under"}-billed vs the order.`;
  return "Needs review.";
}

// A line counts as reconciled when it's a clean match or its variance has been accepted. A disputed /
// credit-note line is recorded but still blocks the bill, so it does NOT count as reconciled.
function lineReconciled(line: MatchLineView): boolean {
  const a = line.resolution?.action;
  if (a === "Accept" || a === "AcceptAndUpdateCatalog") return true;
  return (
    line.fullyAllocated &&
    !line.hasPriceVariance &&
    !line.hasQuantityVariance &&
    !line.hasOverBillingVariance &&
    line.allocations.length > 0
  );
}

function deriveSummary(
  view: MatchGroupView,
): { title: string; body: string; tone: Tone; icon: React.ReactNode } {
  const open = view.lines.filter(
    (l) => (l.hasPriceVariance || l.hasQuantityVariance || l.hasOverBillingVariance) && !l.resolution,
  ).length;
  const disputed = view.lines.some((l) => l.resolution?.action === "Dispute" || l.resolution?.action === "RequestCreditNote");

  if (view.status === "Resolved") {
    return {
      title: "Match resolved",
      body: "Every line is reconciled or its variance accepted. Ready to submit for review.",
      tone: "primary",
      icon: <Check className="size-[18px]" />,
    };
  }
  if (disputed) {
    return {
      title: "Dispute raised",
      body: "One or more lines are disputed or awaiting a credit note. The bill can't be submitted until they're cleared.",
      tone: "destructive",
      icon: <AlertTriangle className="size-[18px]" />,
    };
  }
  if (open > 0) {
    return {
      title: `${open} line${open === 1 ? "" : "s"} need review`,
      body: "Resolve each flagged line above to clear the match: accept the variance, request a credit note, or dispute it.",
      tone: "amber",
      icon: <AlertTriangle className="size-[18px]" />,
    };
  }
  return {
    title: "Matching in progress",
    body: "Match each line to its purchase order and delivery note to complete the match.",
    tone: "muted",
    icon: <ReceiptText className="size-[18px]" />,
  };
}
